import { useId, type PropsWithChildren, type ReactNode } from 'react'
import { ActionGlyph } from '../../components/ActionGlyph'
import type { ActionResult } from '../../domain/models'
import './account-access.css'

interface AccountScaffoldProps extends PropsWithChildren {
  eyebrow: string
  title: string
  intro?: string
  className?: string
  onBack?: () => void
  backLabel?: string
  headerAside?: ReactNode
  headerMedia?: ReactNode
}

export function AccountScaffold({
  backLabel = 'Back',
  children,
  className = '',
  eyebrow,
  headerAside,
  headerMedia,
  intro,
  onBack,
  title,
}: AccountScaffoldProps) {
  const headingId = useId()

  return (
    <section className={`account-page ${className}`.trim()} aria-labelledby={headingId}>
      {headerMedia ? <div className="account-page__media">{headerMedia}</div> : null}
      <div className="account-page__flash" aria-hidden="true" />
      <header className="account-page__header">
        <div className="account-page__toolbar">
          {onBack ? (
            <button className="access-back" type="button" onClick={onBack}>
              <ActionGlyph direction="back" />
              {backLabel}
            </button>
          ) : <span />}
          {headerAside}
        </div>
        <p className="access-eyebrow">{eyebrow}</p>
        <h1
          className={`account-page__title ${title.length > 18 ? 'account-page__title--xlong' : title.length > 10 ? 'account-page__title--long' : ''}`.trim()}
          id={headingId}
        >
          {title}
        </h1>
        {intro ? <p className="account-page__intro">{intro}</p> : null}
      </header>
      <div className="account-page__body">{children}</div>
    </section>
  )
}

interface InlineStatusProps {
  result: ActionResult | null
  id?: string
}

export function InlineStatus({ id, result }: InlineStatusProps) {
  if (!result) return <div className="access-status-slot" id={id} aria-live="polite" />

  return (
    <div
      className={`access-status ${result.ok ? 'access-status--success' : 'access-status--error'}`}
      id={id}
      role={result.ok ? 'status' : 'alert'}
    >
      <strong>{result.ok ? 'Done' : 'Check this'}</strong>
      <span>{result.message}</span>
    </div>
  )
}

interface SettingsLinkProps {
  title: string
  detail: string
  onClick: () => void
  meta?: string
}

export function SettingsLink({ detail, meta, onClick, title }: SettingsLinkProps) {
  return (
    <button className="settings-link" type="button" onClick={onClick}>
      <span className="settings-link__copy">
        <strong>{title}</strong>
        <span>{detail}</span>
      </span>
      <span className="settings-link__end">
        {meta ? <span>{meta}</span> : null}
        <ActionGlyph />
      </span>
    </button>
  )
}

interface PreferenceToggleProps {
  checked: boolean
  description: string
  label: string
  onChange: (checked: boolean) => void
}

export function PreferenceToggle({ checked, description, label, onChange }: PreferenceToggleProps) {
  return (
    <label className="preference-toggle">
      <span>
        <strong>{label}</strong>
        <span>{description}</span>
      </span>
      <input checked={checked} type="checkbox" onChange={(event) => onChange(event.target.checked)} />
      <span className="preference-toggle__control" aria-hidden="true"><span /></span>
    </label>
  )
}
